import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Stack } from '@mui/material';
import { getCountryLabel, STRIPE_LOGO_URL } from './constants';
import {
  formatBankAccountLabel,
  getBankAccountStatusPresentation,
  StripeConnectedBankAccount,
} from './stripe-account-display';
import {
  ActionButton,
  CardDescription,
  CardTitle,
  Divider,
  StatusBadge,
  StatusLabel,
  StatusRow,
  StatusValue,
  StripeCard,
  StripeContainer,
  StripeLogo,
} from './styled';
import { useStripeAccountDashboardLoginUrl } from './use-stripe-account-dashboard-login-url';
import { useStripeAccountOnboardUrl } from './use-stripe-account-onboard-url';

interface StripeConnectedBannerProps {
  bankAccount?: StripeConnectedBankAccount | null;
  chargesEnabled?: boolean;
  country?: string;
  email?: string;
  payoutSchedule?: string;
  detailsSubmitted?: boolean;
}

export const StripeConnectedBanner = ({
  bankAccount,
  chargesEnabled,
  country,
  email,
  payoutSchedule,
  detailsSubmitted,
}: StripeConnectedBannerProps) => {
  const { redirectToStripeOnboard, loading: onboardingLoading } = useStripeAccountOnboardUrl();
  const { openStripeDashboard, loading: dashboardLoading } =
    useStripeAccountDashboardLoginUrl();

  const handleOpenDashboard = () => {
    void openStripeDashboard();
  };

  const handleContinueOnboarding = () => {
    void redirectToStripeOnboard(country);
  };

  const isFullySetup = detailsSubmitted && chargesEnabled;
  const bankAccountLabel = formatBankAccountLabel(bankAccount);
  const bankAccountStatus = getBankAccountStatusPresentation(bankAccount?.status);

  return (
    <StripeContainer>
      <StripeCard>
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <StripeLogo src={STRIPE_LOGO_URL} alt="Stripe" />
            <StatusBadge status={isFullySetup ? 'success' : 'warning'}>
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
              {isFullySetup ? 'Active' : 'Setup Incomplete'}
            </StatusBadge>
          </Stack>

          <CardTitle>Stripe Account Connected</CardTitle>

          <CardDescription>
            {isFullySetup
              ? 'Your Stripe Express account is connected. Payments will be deposited directly to your bank account.'
              : 'Your Stripe account is connected but setup is incomplete. Click below to continue onboarding.'}
          </CardDescription>

          <Divider />

          <Stack gap={1.5}>
            <StatusRow>
              <StatusLabel>Country</StatusLabel>
              <StatusValue>{getCountryLabel(country)}</StatusValue>
            </StatusRow>

            {isFullySetup && email ? (
              <StatusRow>
                <StatusLabel>Account Email</StatusLabel>
                <StatusValue>{email}</StatusValue>
              </StatusRow>
            ) : null}

            {isFullySetup && bankAccountLabel ? (
              <StatusRow>
                <StatusLabel>Bank Account</StatusLabel>
                <StatusValue>
                  <Stack gap={0.5}>
                    <span>{bankAccountLabel}</span>
                    {bankAccountStatus ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        color={
                          bankAccountStatus.tone === 'success'
                            ? 'success.main'
                            : 'warning.main'
                        }
                      >
                        {bankAccountStatus.tone === 'success' ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                        {bankAccountStatus.label}
                      </Stack>
                    ) : null}
                  </Stack>
                </StatusValue>
              </StatusRow>
            ) : null}

            {isFullySetup && payoutSchedule ? (
              <StatusRow>
                <StatusLabel>Payout Schedule</StatusLabel>
                <StatusValue>{payoutSchedule}</StatusValue>
              </StatusRow>
            ) : null}

            <StatusRow>
              <StatusLabel>Charges</StatusLabel>
              <StatusValue>
                {chargesEnabled ? (
                  <Stack direction="row" alignItems="center" gap={0.5} color="success.main">
                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    Enabled
                  </Stack>
                ) : (
                  'Not enabled'
                )}
              </StatusValue>
            </StatusRow>
          </Stack>

          {isFullySetup ? (
            <ActionButton
              variant="contained"
              onClick={handleOpenDashboard}
              disabled={dashboardLoading}
              endIcon={<OpenInNewIcon />}
            >
              {dashboardLoading ? 'Opening...' : 'Open Stripe Dashboard'}
            </ActionButton>
          ) : (
            <ActionButton
              variant="contained"
              onClick={handleContinueOnboarding}
              disabled={onboardingLoading}
            >
              {onboardingLoading ? 'Loading...' : 'Continue Onboarding'}
            </ActionButton>
          )}
        </Stack>
      </StripeCard>
    </StripeContainer>
  );
};
